/**
 * 主题配置
 * 统一管理所有颜色和样式变量
 */

// 导入CSS变量
import "./variables.css";

export interface ThemeConfig {
  // 基础色板（Color Palette）
  palette: {
    // 灰色系
    gray50: string; // 最浅灰
    gray100: string; // 浅灰
    gray200: string; // 中浅灰
    gray300: string; // 中灰
    gray400: string; // 深灰
    gray500: string; // 更深灰
    gray900: string; // 最深灰/黑
    // 蓝色系
    blue50: string; // 浅蓝
    blue500: string; // 主蓝
    blue700: string; // 深蓝
  };
  // 主色调
  colors: {
    primary: string;
    primaryLight: string;
    primaryDark: string;
  };
  // 布局背景色
  layout: {
    background: string;
    siderBackground: string;
    headerBackground: string;
    tabsBackground: string;
    siderShadow: string;
  };
  // 菜单相关
  menu: {
    // 未选中状态
    itemColor: string;
    itemIconColor: string;
    itemFontWeight: number;
    itemFontSize: number;
    itemLineHeight: number;
    iconSize: number;
    // 选中状态
    selectedBg: string;
    selectedColor: string;
    selectedIconColor: string;
    selectedFontWeight: number;
    // 悬停状态
    hoverBg: string;
  };
  // 文字颜色
  text: {
    primary: string;
    secondary: string;
    disabled: string;
    logo: string;
  };
  // 边框和阴影
  border: {
    color: string;
    radius: number;
  };
  shadow: {
    small: string;
    medium: string;
    large: string;
  };
}

/**
 * 默认主题配置
 */
export const defaultTheme: ThemeConfig = {
  // 基础色板
  palette: {
    // 灰色系
    gray50: "#f8fafc", // 主灰色（布局背景）
    gray100: "#e2e8f0", // 浅灰（边框、分隔线）
    gray200: "#cbd5e1", // 中浅灰（未选中图标）
    gray300: "#94a3b8", // 中灰（悬停背景）
    gray400: "#64748b", // 深灰
    gray500: "#475569", // 更深灰
    gray900: "#0f172a", // 最深灰/黑（文字）
    // 蓝色系
    blue50: "#E9FAFF", // 浅蓝（选中背景）
    blue500: "#08C4C8", // 主蓝（主色调）
    blue700: "#1d4ed8", // 深蓝
  },
  colors: {
    primary: "#08C4C8", // 引用 palette.blue500
    primaryLight: "#E9FAFF", // 引用 palette.blue50
    primaryDark: "#1d4ed8", // 引用 palette.blue700
  },
  layout: {
    background: "#f8fafc", // 引用 palette.gray50
    siderBackground: "#ffffff",
    headerBackground: "#ffffff",
    tabsBackground: "#f8fafc",
    siderShadow: "inset -1px 0px 0px 0px #e2e8f0",
  },
  menu: {
    // 未选中状态
    itemColor: "#475569",
    itemIconColor: "#475569",
    itemFontWeight: 600,
    itemFontSize: 14,
    itemLineHeight: 20,
    iconSize: 20,
    // 选中状态
    selectedBg: "#E9FAFF",
    selectedColor: "#08C4C8",
    selectedIconColor: "#08C4C8",
    selectedFontWeight: 700,
    // 悬停状态
    hoverBg: "#f1f5f9",
  },
  text: {
    primary: "#0f172a",
    secondary: "#475569",
    disabled: "rgba(15, 23, 42, 0.35)",
    logo: "#626A7C",
  },
  border: {
    color: "#e2e8f0",
    radius: 12,
  },
  shadow: {
    small: "0 1px 2px rgba(15, 23, 42, 0.06)",
    medium: "0 4px 10px -4px rgba(15, 23, 42, 0.15)",
    large: "0 12px 30px -12px rgba(15, 23, 42, 0.25)",
  },
};

/**
 * 将主题配置应用到CSS变量
 */
export const applyTheme = (theme: ThemeConfig = defaultTheme) => {
  const root = document.documentElement;

  // 基础色板
  root.style.setProperty("--palette-gray-50", theme.palette.gray50);
  root.style.setProperty("--palette-gray-100", theme.palette.gray100);
  root.style.setProperty("--palette-gray-200", theme.palette.gray200);
  root.style.setProperty("--palette-gray-300", theme.palette.gray300);
  root.style.setProperty("--palette-gray-400", theme.palette.gray400);
  root.style.setProperty("--palette-gray-500", theme.palette.gray500);
  root.style.setProperty("--palette-gray-900", theme.palette.gray900);
  root.style.setProperty("--palette-blue-50", theme.palette.blue50);
  root.style.setProperty("--palette-blue-500", theme.palette.blue500);
  root.style.setProperty("--palette-blue-700", theme.palette.blue700);

  // 主色调
  root.style.setProperty("--theme-primary", theme.colors.primary);
  root.style.setProperty("--theme-primary-light", theme.colors.primaryLight);
  root.style.setProperty("--theme-primary-dark", theme.colors.primaryDark);
  root.style.setProperty("--ifa-primary", theme.colors.primary);
  root.style.setProperty("--ifa-primary-dark", theme.colors.primaryDark);

  // 布局背景
  root.style.setProperty("--theme-layout-bg", theme.layout.background);
  root.style.setProperty("--theme-sider-bg", theme.layout.siderBackground);
  root.style.setProperty("--theme-header-bg", theme.layout.headerBackground);
  root.style.setProperty("--theme-tabs-bg", theme.layout.tabsBackground);
  root.style.setProperty("--theme-sider-shadow", theme.layout.siderShadow);

  // 菜单
  root.style.setProperty("--theme-menu-item-color", theme.menu.itemColor);
  root.style.setProperty("--theme-menu-icon-color", theme.menu.itemIconColor);
  root.style.setProperty(
    "--theme-menu-font-weight",
    String(theme.menu.itemFontWeight),
  );
  root.style.setProperty(
    "--theme-menu-font-size",
    `${theme.menu.itemFontSize}px`,
  );
  root.style.setProperty(
    "--theme-menu-line-height",
    `${theme.menu.itemLineHeight}px`,
  );
  root.style.setProperty("--theme-menu-icon-size", `${theme.menu.iconSize}px`);
  root.style.setProperty("--theme-menu-selected-bg", theme.menu.selectedBg);
  root.style.setProperty(
    "--theme-menu-selected-color",
    theme.menu.selectedColor,
  );
  root.style.setProperty(
    "--theme-menu-selected-icon-color",
    theme.menu.selectedIconColor,
  );
  root.style.setProperty(
    "--theme-menu-selected-font-weight",
    String(theme.menu.selectedFontWeight),
  );
  root.style.setProperty("--theme-menu-hover-bg", theme.menu.hoverBg);

  // 文字
  root.style.setProperty("--theme-text-primary", theme.text.primary);
  root.style.setProperty("--theme-text-secondary", theme.text.secondary);
  root.style.setProperty("--theme-text-disabled", theme.text.disabled);
  root.style.setProperty("--theme-text-logo", theme.text.logo);

  // 边框和阴影
  root.style.setProperty("--theme-border-color", theme.border.color);
  root.style.setProperty("--theme-border-radius", `${theme.border.radius}px`);
  root.style.setProperty("--theme-shadow-small", theme.shadow.small);
  root.style.setProperty("--theme-shadow-medium", theme.shadow.medium);
  root.style.setProperty("--theme-shadow-large", theme.shadow.large);
};

/**
 * 获取当前主题配置
 */
export const getCurrentTheme = (): ThemeConfig => {
  return defaultTheme;
};

// 表单元素主题色（统一使用主色）
const FORM_THEME_COLOR = "#08C4C8";

/**
 * 导出给 Ant Design 使用的主题配置
 */
export const getAntdTheme = (theme: ThemeConfig = defaultTheme) => {
  return {
    token: {
      fontFamily: 'NotoSans, "Noto Sans", sans-serif',
    },
    components: {
      Button: {
        colorPrimary: theme.colors.primary,
        colorPrimaryHover: "#08c4c8", // 主按钮悬停颜色
        colorPrimaryActive: "#06a09a", // 主按钮激活颜色（稍深）
        colorLink: "#08C4C8",
        colorLinkHover: "#08c4c8", // Link 按钮悬停颜色
        colorLinkActive: "#06a09a", // Link 按钮激活颜色（稍深）
        colorText: "#475569",
        colorBorder: "#E2E8F0",
        algorithm: true,
      },
      Input: {
        colorPrimary: FORM_THEME_COLOR,
        colorPrimaryHover: FORM_THEME_COLOR,
      },
      Select: {
        colorPrimary: FORM_THEME_COLOR,
        colorPrimaryHover: FORM_THEME_COLOR,
      },
      InputNumber: {
        colorPrimary: FORM_THEME_COLOR,
        colorPrimaryHover: FORM_THEME_COLOR,
      },
      DatePicker: {
        colorPrimary: FORM_THEME_COLOR,
        colorPrimaryHover: FORM_THEME_COLOR,
      },
      TimePicker: {
        colorPrimary: FORM_THEME_COLOR,
        colorPrimaryHover: FORM_THEME_COLOR,
      },
      Checkbox: {
        colorPrimary: FORM_THEME_COLOR,
        colorPrimaryHover: FORM_THEME_COLOR,
      },
      Radio: {
        colorPrimary: FORM_THEME_COLOR,
        colorPrimaryHover: FORM_THEME_COLOR,
      },
      Switch: {
        colorPrimary: FORM_THEME_COLOR,
        colorPrimaryHover: FORM_THEME_COLOR,
      },
      Cascader: {
        colorPrimary: FORM_THEME_COLOR,
        colorPrimaryHover: FORM_THEME_COLOR,
      },
      TreeSelect: {
        colorPrimary: FORM_THEME_COLOR,
        colorPrimaryHover: FORM_THEME_COLOR,
      },
      Table: {
        headerBg: "#F9FAFB",
        headerColor: "#475569",
        headerFontSize: 12,
        headerFontWeight: 600,
        cellPaddingBlockMD: 9,
      },
      Modal: {
        titleColor: "#09090B",
      },
      Form: {
        labelColor: "#334255",
        labelFontSize: 12,
        labelHeight: 17,
        verticalLabelPadding: "0 0 5px",
        itemMarginBottom: 22,
      },
      Tabs: {
        itemColor: "#475569", // 未选中标签颜色
        itemSelectedColor: FORM_THEME_COLOR, // 选中标签颜色
        itemHoverColor: FORM_THEME_COLOR, // 悬停标签颜色
        itemActiveColor: FORM_THEME_COLOR, // 激活标签颜色（active状态）
        inkBarColor: FORM_THEME_COLOR, // 下划线颜色
      },
      Upload: {
        colorPrimaryHover: FORM_THEME_COLOR,
      },
    },
  };
};

/**
 * 便捷方法：同时应用主题并返回 Ant Design 需要的配置
 */
export const setupTheme = (theme: ThemeConfig = defaultTheme) => {
  applyTheme(theme);
  return getAntdTheme(theme);
};
